import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Shield, RefreshCw, Save, CheckCircle, AlertCircle, Lock, Users, Loader2 } from 'lucide-react';
import { useSiteConfigValue } from '@/hooks/useSiteConfigValue';
import { useSiteConfig } from '@/hooks/useSiteConfig';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
const AdminSiteConfigSecuritySettings = () => {
  const {
    toast: toastHook
  } = useToast();
  const [saving, setSaving] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [guestStats, setGuestStats] = useState({
    totalIPs: 0,
    totalGenerations: 0,
    todayGenerations: 0,
    claimedImages: 0
  });

  // Guest generation configuration hooks
  const {
    value: guestGenerationEnabledValue
  } = useSiteConfigValue('guest_generation_enabled', 'true');
  const {
    value: guestGenerationLimitValue
  } = useSiteConfigValue('guest_generation_daily_limit', '5');
  const {
    value: guestSessionDurationValue
  } = useSiteConfigValue('guest_session_duration_hours', '24');
  const {
    value: simplePasswordModeValue
  } = useSiteConfigValue('password_policy_simple_mode', 'false');
  const {
    updateConfiguration
  } = useSiteConfig();
  const [guestGenerationEnabled, setGuestGenerationEnabled] = useState(guestGenerationEnabledValue === 'true');
  const [guestGenerationLimit, setGuestGenerationLimit] = useState(guestGenerationLimitValue || '5');
  const [guestSessionDuration, setGuestSessionDuration] = useState(guestSessionDurationValue || '24');
  const [simplePasswordMode, setSimplePasswordMode] = useState(simplePasswordModeValue === 'true');

  // Update local state when config values change
  useEffect(() => {
    setGuestGenerationEnabled(guestGenerationEnabledValue === 'true');
  }, [guestGenerationEnabledValue]);
  useEffect(() => {
    setGuestGenerationLimit(guestGenerationLimitValue || '5');
  }, [guestGenerationLimitValue]);
  useEffect(() => {
    setGuestSessionDuration(guestSessionDurationValue || '24');
  }, [guestSessionDurationValue]);
  useEffect(() => {
    setSimplePasswordMode(simplePasswordModeValue === 'true');
  }, [simplePasswordModeValue]);

  // Load guest statistics via Edge Function (admin-only)
  useEffect(() => {
    const loadGuestStats = async () => {
      try {
        setStatsLoading(true);
        const {
          data,
          error
        } = await supabase.functions.invoke('get-guest-stats');
        if (error) throw error as any;
        if (data) {
          setGuestStats({
            totalIPs: data.totalIPs ?? 0,
            totalGenerations: data.totalGenerations ?? 0,
            todayGenerations: data.todayGenerations ?? 0,
            claimedImages: data.claimedImages ?? 0
          });
        }
      } catch (error) {
        console.error('Failed to load guest statistics:', error);
      } finally {
        setStatsLoading(false);
      }
    };
    loadGuestStats();
  }, []);
  const handleResetTodayLimits = async () => {
    setIsSaving(true);
    try {
      const {
        data,
        error
      } = await supabase.functions.invoke('reset-guest-daily-limits');
      if (error) throw error as any;

      // Small delay then refresh stats
      await new Promise(r => setTimeout(r, 300));
      const {
        data: stats
      } = await supabase.functions.invoke('get-guest-stats');
      if (stats) {
        setGuestStats({
          totalIPs: stats.totalIPs ?? 0,
          totalGenerations: stats.totalGenerations ?? 0,
          todayGenerations: stats.todayGenerations ?? 0,
          claimedImages: stats.claimedImages ?? 0
        });
      }
      toast.success('Daily guest limits have been reset successfully');
    } catch (error) {
      toast.error('Failed to reset daily guest limits');
      console.error('Reset limits error:', error);
    } finally {
      setIsSaving(false);
    }
  };
  const refreshGuestStats = async () => {
    try {
      setStatsLoading(true);
      const {
        data
      } = await supabase.functions.invoke('get-guest-stats');
      if (data) {
        setGuestStats({
          totalIPs: data.totalIPs ?? 0,
          totalGenerations: data.totalGenerations ?? 0,
          todayGenerations: data.todayGenerations ?? 0,
          claimedImages: data.claimedImages ?? 0
        });
      }
    } catch (e) {
      console.error('Refresh guest stats failed', e);
    } finally {
      setStatsLoading(false);
    }
  };
  const handleSavePasswordPolicy = async () => {
    setIsSaving(true);
    try {
      await updateConfiguration('password_policy_simple_mode', simplePasswordMode.toString(), `Password policy updated to ${simplePasswordMode ? 'simple' : 'complex'} mode`);
      toast.success('Password policy updated successfully');
    } catch (error) {
      toast.error('Failed to update password policy');
      console.error('Password policy update error:', error);
    } finally {
      setIsSaving(false);
    }
  };
  const handleSaveGuestSettings = async () => {
    setIsSaving(true);
    try {
      await Promise.all([updateConfiguration('guest_generation_enabled', guestGenerationEnabled.toString(), `Guest generation ${guestGenerationEnabled ? 'enabled' : 'disabled'}`), updateConfiguration('guest_generation_daily_limit', guestGenerationLimit, `Guest generation daily limit set to ${guestGenerationLimit} per day`), updateConfiguration('guest_session_duration_hours', guestSessionDuration, `Guest session duration set to ${guestSessionDuration} hours`)]);
      toast.success('Guest settings updated successfully');
      await refreshGuestStats();
    } catch (error) {
      toast.error('Failed to update guest settings');
      console.error('Guest settings update error:', error);
    } finally {
      setIsSaving(false);
    }
  };
  const securityConfigs = [{
    key: 'enable_recaptcha',
    label: 'Enable reCAPTCHA',
    type: 'boolean',
    description: 'Enable reCAPTCHA for forms to prevent spam'
  }, {
    key: 'recaptcha_site_key',
    label: 'reCAPTCHA Site Key',
    type: 'input',
    placeholder: '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI',
    sensitive: true
  }, {
    key: 'session_timeout',
    label: 'Session Timeout (minutes)',
    type: 'number',
    placeholder: '1440'
  }, {
    key: 'password_min_length',
    label: 'Minimum Password Length',
    type: 'number',
    placeholder: '8'
  }, {
    key: 'security_max_login_attempts',
    label: 'Max Login Attempts',
    type: 'number',
    placeholder: '5',
    description: 'Maximum failed login attempts before lockout'
  }, {
    key: 'security_enable_audit_log',
    label: 'Enable Security Audit Logging',
    type: 'boolean',
    description: 'Log all security-related events for monitoring'
  }, {
    key: 'security_api_key_encryption',
    label: 'API Key Encryption',
    type: 'boolean',
    description: 'Encrypt API keys before storing in database (recommended)'
  }, {
    key: 'password_policy_simple_mode',
    label: 'Simple Password Mode',
    type: 'boolean',
    description: 'Enable simple password policy (6+ characters) instead of complex requirements'
  }, {
    key: 'guest_generation_enabled',
    label: 'Guest Generation Enabled',
    type: 'boolean',
    description: 'Allow users to generate images without creating an account'
  }, {
    key: 'guest_generation_daily_limit',
    label: 'Guest Daily Limit',
    type: 'number',
    placeholder: '5',
    description: 'Maximum images each IP can generate per day'
  }];
  const SecurityConfigCard = ({
    config
  }: {
    config: typeof securityConfigs[0];
  }) => {
    const {
      value,
      loading,
      refetch
    } = useSiteConfigValue(config.key);
    const [localValue, setLocalValue] = useState('');
    const [hasChanged, setHasChanged] = useState(false);
    const [showSensitive, setShowSensitive] = useState(false);
    React.useEffect(() => {
      if (value !== null && value !== localValue) {
        setLocalValue(value);
        setHasChanged(false);
      }
    }, [value]);
    const handleInputChange = (newValue: string) => {
      setLocalValue(newValue);
      setHasChanged(newValue !== (value || ''));
    };
    const handleBooleanChange = (checked: boolean) => {
      const newValue = checked.toString();
      setLocalValue(newValue);
      setHasChanged(newValue !== (value || 'false'));
    };
    const handleUpdate = async () => {
      setSaving(config.key);
      try {
        console.log(`Updating ${config.key} to:`, config.sensitive ? '[REDACTED]' : localValue);
        const {
          error
        } = await supabase.rpc('update_site_config', {
          config_key: config.key,
          new_value: localValue,
          admin_email: null,
          change_reason: `Updated ${config.label} via Admin Panel`
        });
        if (error) throw error;
        toastHook({
          title: "Success",
          description: `${config.label} updated successfully`
        });
        setHasChanged(false);
        await refetch();
      } catch (error) {
        console.error(`Error updating ${config.key}:`, error);
        toastHook({
          title: "Error",
          description: `Failed to update ${config.label}`,
          variant: "destructive"
        });
      } finally {
        setSaving(null);
      }
    };
    const handleRefresh = async () => {
      console.log(`Refreshing ${config.key}...`);
      await refetch();
    };
    const displayValue = (val: string) => {
      if (config.sensitive && val && !showSensitive) {
        return '•'.repeat(Math.min(val.length, 20));
      }
      return val;
    };
    const isSecurityCritical = config.key === 'password_min_length' && parseInt(localValue) < 8;
    return <Card className={`bg-white/10 backdrop-blur-md border-white/20 ${config.sensitive ? 'border-purple-500/30' : ''}`}>
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-sm font-medium flex items-center justify-between">
            <span className="flex items-center space-x-2">
              {config.label}
              {config.sensitive && <Lock className="w-3 h-3 text-purple-400" />}
            </span>
            <div className="flex items-center space-x-2">
              {config.sensitive && <Button variant="ghost" size="sm" onClick={() => setShowSensitive(!showSensitive)} className="text-purple-400 hover:text-purple-300 p-1">
                  {showSensitive ? '👁️' : '👁️‍🗨️'}
                </Button>}
              {loading ? <div className="w-4 h-4 border border-white/20 border-t-white/60 rounded-full animate-spin" /> : <Button variant="ghost" size="sm" onClick={handleRefresh} className="text-gray-400 hover:text-white p-1">
                  <RefreshCw className="w-3 h-3" />
                </Button>}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-white text-xs">
                {config.type === 'boolean' ? 'Status' : 'Current Value'}
              </Label>
              <div className="flex items-center space-x-1">
                {hasChanged && <Badge className="bg-orange-600/20 text-orange-400 text-xs">Modified</Badge>}
                {config.sensitive && <Badge className="bg-purple-600/20 text-purple-400 text-xs">Sensitive</Badge>}
                {isSecurityCritical && <Badge className="bg-red-600/20 text-red-400 text-xs">Weak</Badge>}
                {config.type === 'boolean' ? localValue === 'true' ? <CheckCircle className="w-3 h-3 text-green-400" /> : <AlertCircle className="w-3 h-3 text-gray-400" /> : localValue ? <CheckCircle className="w-3 h-3 text-green-400" /> : <AlertCircle className="w-3 h-3 text-gray-400" />}
              </div>
            </div>
            
            {config.type === 'boolean' ? <div className="flex items-center space-x-3">
                <Switch checked={localValue === 'true'} onCheckedChange={handleBooleanChange} disabled={saving === config.key} />
                <span className="text-white text-sm">
                  {localValue === 'true' ? 'Enabled' : 'Disabled'}
                </span>
              </div> : <Input type={config.type === 'number' ? 'number' : config.sensitive && !showSensitive ? 'password' : 'text'} value={displayValue(localValue)} onChange={e => handleInputChange(e.target.value)} placeholder={config.placeholder} disabled={saving === config.key} className={`bg-gray-800 border-gray-600 text-white text-sm ${isSecurityCritical ? 'border-red-500/50' : ''}`} />}
            
            {config.description && <p className="text-xs text-gray-400">{config.description}</p>}
            
            {isSecurityCritical && <p className="text-xs text-red-400">⚠️ Password length below 8 characters is not secure</p>}
          </div>

          <div className="flex items-center justify-between">
            <div className="text-xs text-gray-400">
              {loading ? 'Loading...' : `Loaded: ${value ? config.sensitive && !showSensitive ? '[HIDDEN]' : value : 'Not set'}`}
            </div>
            <Button onClick={handleUpdate} disabled={!hasChanged || saving === config.key} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">
              {saving === config.key ? <div className="w-3 h-3 border border-white/20 border-t-white/60 rounded-full animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
              Update
            </Button>
          </div>

          <div className="text-xs text-gray-500 space-y-1">
            <div>Debug Info:</div>
            <div>• Key: {config.key}</div>
            <div>• Current: {value ? config.sensitive && !showSensitive ? '[HIDDEN]' : value : 'null'}</div>
            <div>• Local: {localValue ? config.sensitive && !showSensitive ? '[HIDDEN]' : localValue : 'empty'}</div>
            <div>• Changed: {hasChanged ? 'yes' : 'no'}</div>
          </div>
        </CardContent>
      </Card>;
  };
  return <div className="space-y-6">
      <Card className="bg-white/10 backdrop-blur-md border-white/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center space-x-2">
            <Shield className="w-5 h-5" />
            <span>Security Configuration</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {securityConfigs.map(config => <SecurityConfigCard key={config.key} config={config} />)}
          </div>
        </CardContent>
      </Card>

      {/* Password Policy Settings */}
      <Card className="bg-white/10 backdrop-blur-md border-white/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center space-x-2">
            <Lock className="w-5 h-5" />
            <span>Password Policy Settings</span>
          </CardTitle>
          <CardDescription className="text-gray-400">
            Configure password requirements for user accounts
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-white">Simple Password Mode</Label>
              <div className="text-sm text-gray-400">
                Enable simplified password requirements for easier user access
              </div>
            </div>
            <Switch checked={simplePasswordMode} onCheckedChange={setSimplePasswordMode} />
          </div>
          
          <Button onClick={handleSavePasswordPolicy} disabled={isSaving} variant="outline" className="text-white border-gray-600 hover:bg-gray-700">
            {isSaving ? <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </> : 'Save Password Policy'}
          </Button>
        </CardContent>
      </Card>

      {/* Guest Generation Settings */}
      <Card className="bg-white/10 backdrop-blur-md border-white/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center space-x-2">
            <Users className="w-5 h-5" />
            <span>Guest Image Generation</span>
          </CardTitle>
          <CardDescription className="text-gray-400">
            Configure settings for users generating images without login
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-white">Enable Guest Generation</Label>
              <div className="text-sm text-gray-400">
                Allow users to generate images without creating an account
              </div>
            </div>
            <Switch checked={guestGenerationEnabled} onCheckedChange={setGuestGenerationEnabled} />
          </div>

          <div className="space-y-2">
            <Label className="text-white">Daily Generation Limit per IP</Label>
            <Select value={guestGenerationLimit} onValueChange={setGuestGenerationLimit}>
              <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                <SelectValue placeholder="Select limit" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 image per day</SelectItem>
                <SelectItem value="3">3 images per day</SelectItem>
                <SelectItem value="5">5 images per day</SelectItem>
                <SelectItem value="10">10 images per day</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-sm text-gray-400">
              Maximum number of images each IP address can generate per day
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-white">Guest Session Duration</Label>
            <Select value={guestSessionDuration} onValueChange={setGuestSessionDuration}>
              <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                <SelectValue placeholder="Select duration" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="12">12 hours</SelectItem>
                <SelectItem value="24">24 hours</SelectItem>
                <SelectItem value="48">48 hours</SelectItem>
                <SelectItem value="72">72 hours</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-sm text-gray-400">
              How long guest images remain claimable after signup
            </div>
          </div>

          <Button onClick={handleSaveGuestSettings} disabled={isSaving} variant="outline" className="text-white border-gray-600 bg-rose-950 hover:bg-rose-800">
            {isSaving ? <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </> : 'Save Guest Settings'}
          </Button>

          {/* Guest Statistics */}
          <div className="pt-4 border-t border-gray-600">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-base text-white">Guest Usage Statistics</Label>
              <div className="flex items-center gap-2">
                <Button onClick={refreshGuestStats} disabled={statsLoading} variant="outline" size="sm" className="text-white border-gray-600 bg-slate-950 hover:bg-slate-800">
                  {statsLoading ? <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Refreshing...
                    </> : 'Refresh'}
                </Button>
                <Button onClick={handleResetTodayLimits} disabled={isSaving} variant="destructive" size="sm" className="bg-red-600 hover:bg-red-700 text-white">
                  {isSaving ? <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Resetting...
                    </> : 'Reset Today\'s Limits'}
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-2">
              <div className="text-center p-3 bg-gray-800/50 rounded-lg">
                <div className="text-2xl font-bold text-blue-400">{guestStats.totalIPs}</div>
                <div className="text-xs text-gray-400">Unique IPs Today</div>
              </div>
              <div className="text-center p-3 bg-gray-800/50 rounded-lg">
                <div className="text-2xl font-bold text-green-400">{guestStats.totalGenerations}</div>
                <div className="text-xs text-gray-400">Total Generations</div>
              </div>
              <div className="text-center p-3 bg-gray-800/50 rounded-lg">
                <div className="text-2xl font-bold text-yellow-400">{guestStats.todayGenerations}</div>
                <div className="text-xs text-gray-400">Today's Generations</div>
              </div>
              <div className="text-center p-3 bg-gray-800/50 rounded-lg">
                <div className="text-2xl font-bold text-purple-400">{guestStats.claimedImages}</div>
                <div className="text-xs text-gray-400">Images Claimed</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>;
};
export default AdminSiteConfigSecuritySettings;